import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/shared/ui/Button';
import { Paginacao } from '@/shared/ui/Paginacao';
import { authApi, type Perfil, type ResumoMaquina } from '../api/authApi';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { usePaginacao } from '@/shared/hooks/usePaginacao';

type Props = {
  maquinaAtualId: string | null;
};

export function MinhasMaquinasPage({ maquinaAtualId }: Props) {
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [removendoId, setRemovendoId] = useState<string | null>(null);
  const dialogos = useDialogos();

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const p = await authApi.me();
      setPerfil(p);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falha ao carregar perfil');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function remover(m: ResumoMaquina) {
    const ehAtual = m.id === maquinaAtualId;
    const ok = await dialogos.confirmar({
      titulo: ehAtual ? 'Remover máquina atual' : 'Remover máquina',
      mensagem: ehAtual
        ? 'Esta é a MÁQUINA ATUAL. Remover vai deslogar você imediatamente.\n\nContinuar?'
        : `Remover a máquina "${m.nome || 'sem nome'}"?\n\nO acesso desta máquina será revogado.`,
      textoConfirmar: 'Remover',
      perigoso: true,
    });
    if (!ok) return;
    setRemovendoId(m.id);
    setErro(null);
    try {
      await authApi.removerMaquina(m.id);
      if (ehAtual) {
        // Servidor revogou nossa sessão; recarrega a página para forçar tela de login.
        window.location.reload();
        return;
      }
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'falha ao remover');
    } finally {
      setRemovendoId(null);
    }
  }

  const { pagina, setPagina, paginados, total, totalPaginas, tamanhoPagina } =
    usePaginacao(perfil?.maquinas ?? [], 25);

  if (carregando) return <div className="card">Carregando…</div>;
  if (erro) return <div className="card" style={{ color: '#b91c1c' }}>{erro}</div>;
  if (!perfil) return null;

  const ativas = perfil.maquinas.filter((m) => m.ativa);

  return (
    <div className="card" style={{ padding: 20 }}>
      <h2 style={{ marginTop: 0 }}>Minhas máquinas</h2>
      <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4 }}>
        Esta conta pode ter no máximo <strong>{perfil.usuario.maxMaquinas}</strong> máquinas
        ativas simultaneamente. Atualmente: <strong>{ativas.length}</strong>.
      </p>

      <table className="table table--responsive" style={{ width: '100%', marginTop: 16 }}>
        <thead>
          <tr>
            <th>Nome</th>
            <th>Último login</th>
            <th>Cadastrada em</th>
            <th>Status</th>
            <th aria-label="ações" />
          </tr>
        </thead>
        <tbody>
          {paginados.map((m) => {
            const ehAtual = m.id === maquinaAtualId;
            return (
              <tr key={m.id}>
                <td className="table__primary">
                  {m.nome || <em style={{ color: '#9ca3af' }}>sem nome</em>}
                  {ehAtual && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        background: '#dbeafe',
                        color: '#1e40af',
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                    >
                      esta máquina
                    </span>
                  )}
                </td>
                <td data-label="Último login">
                  {m.ultimoLoginEm
                    ? new Date(m.ultimoLoginEm).toLocaleString('pt-BR')
                    : '—'}
                </td>
                <td data-label="Cadastrada em">{new Date(m.criadoEm).toLocaleDateString('pt-BR')}</td>
                <td data-label="Status">
                  {m.ativa ? (
                    <span style={{ color: '#15803d' }}>ativa</span>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>inativa</span>
                  )}
                </td>
                <td className="row-actions" style={{ textAlign: 'right' }}>
                  {m.ativa && (
                    <Button
                      variante="ghost"
                      onClick={() => remover(m)}
                      disabled={removendoId === m.id}
                    >
                      {removendoId === m.id ? '...' : 'Remover'}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <Paginacao
        pagina={pagina}
        totalPaginas={totalPaginas}
        total={total}
        tamanhoPagina={tamanhoPagina}
        onMudarPagina={setPagina}
      />
    </div>
  );
}
